import React, { useState } from 'react';
import styles from './Summary.module.css';
import type { Option, Section, Currency, DefaultOption, SelectedOption } from '../../types';
import { useCurrency } from '../../contexts/CurrencyContext';
import { useLocale } from '../../contexts/LocaleContext';
import { formatPrice } from '../../utils/calculations';
import { getLocalizedString, formatDeadlineLocalized } from '../../utils/localization';
import { t } from '../../locales/translations';
import { sendProjectSummary } from '../../services/telegramApi';
import { downloadProjectDocx } from '../../services/docxGenerator';
import { useQuestions } from '../../hooks/useQuestions';

interface SummaryProps {
  companyName: string;
  sections: Section[];
  selectedOptions: Option[];
  selectedOptionsWithQuantities?: SelectedOption[];
  defaultOptions: DefaultOption[];
  totalPrices: {
    [Currency.USD]: number;
    [Currency.EUR]: number;
    [Currency.KZT]: number;
  };
  projectDeadline: number;
  hourlyOptions: Option[];
  validateSelections: () => {
    isValid: boolean;
    missingSectionIds: string[];
    missingSections: Section[];
  };
}

export const Summary: React.FC<SummaryProps> = ({
  companyName,
  sections,
  selectedOptions,
  selectedOptionsWithQuantities = [],
  defaultOptions,
  totalPrices,
  projectDeadline,
  hourlyOptions,
  validateSelections
}) => {
  const { selectedCurrency } = useCurrency();
  const { selectedLocale } = useLocale();
  const { getFormattedResponses, validateResponses } = useQuestions();
  const [isSending, setIsSending] = useState(false);
  const [sendStatus, setSendStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState<string>('');

  const handleSendToTelegram = async () => {
    setIsSending(true);
    setSendStatus('idle');
    setErrorMessage('');

    try {
      // Prepare selected options map with quantities
      const selectedOptionsMap = new Map<string, { option: Option; quantity: number }>();

      // Add fixed price options with quantities
      fixedPriceOptions.forEach(option => {
        const withQuantity = selectedOptionsWithQuantities.find(item => item.option.id === option.id);
        const quantity = withQuantity ? withQuantity.quantity : 1;
        selectedOptionsMap.set(option.id, { option, quantity });
      });

      // Add hourly options with quantities
      hourlyOptions.forEach(option => {
        const withQuantity = selectedOptionsWithQuantities.find(item => item.option.id === option.id);
        const quantity = withQuantity ? withQuantity.quantity : 1;
        selectedOptionsMap.set(option.id, { option, quantity });
      });

      // Generate and download DOCX file
      await downloadProjectDocx({
        companyName,
        sections,
        selectedOptions: selectedOptionsMap,
        totalPrice: totalPrices,
        projectDeadline,
        locale: selectedLocale,
        currency: selectedCurrency,
      });

      // Get question responses
      const questionResponses = getFormattedResponses(selectedLocale);

      // Then send to Telegram (including questions)
      await sendProjectSummary({
        companyName,
        selectedOptions: fixedPriceOptions,
        hourlyOptions,
        sections,
        totalPrice: totalPrices[selectedCurrency],
        currency: selectedCurrency,
        projectDeadline,
        locale: selectedLocale,
        questionResponses, // Add question responses to the payload
      });

      setSendStatus('success');

      // Reset success message after 3 seconds
      setTimeout(() => {
        setSendStatus('idle');
      }, 3000);
    } catch (error) {
      setSendStatus('error');
      setErrorMessage(error instanceof Error ? error.message : 'Failed to send message');
    } finally {
      setIsSending(false);
    }
  };

  // Filter out hourly options from regular selections
  const fixedPriceOptions = selectedOptions.filter(opt => !opt.is_per_hour);

  const groupedSelections = sections.map(section => ({
    section,
    options: fixedPriceOptions.filter(opt => opt.section_id === section.id)
  })).filter(group => group.options.length > 0);

  const hasAnySelections = selectedOptions.length > 0 || defaultOptions.length > 0;

  // Get validation results
  const validation = validateSelections();
  const { isValid, missingSections } = validation;

  // Get question responses and validation
  const questionResponses = getFormattedResponses(selectedLocale);
  const questionsValidation = validateResponses();

  // Disable button if validation fails or is sending
  const isButtonDisabled = !isValid || isSending || !questionsValidation.isValid;

  if (!hasAnySelections) {
    return (
      <div className={styles.summary}>
        <h2 className={styles.title}>{t('projectSummary', selectedLocale)}</h2>
        <div className={styles.empty}>
          <p className={styles.emptyText}>{t('noOptionsSelected', selectedLocale)}</p>
          <p className={styles.emptySubtext}>{t('startSelecting', selectedLocale)}</p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.summary}>
      <h2 className={styles.title}>{t('projectSummary', selectedLocale)}</h2>

      <div className={styles.selections}>
        {/* Question Responses Section */}
        {questionResponses.length > 0 && (
          <div className={styles.group}>
            <h3 className={styles.groupTitle}>
              {selectedLocale === 'en' ? 'Project Details' :
               selectedLocale === 'ru' ? 'Детали проекта' :
               'Жоба мәліметтері'}
            </h3>
            <div className={styles.questionsResponses}>
              {questionResponses.map((item, index) => (
                <div key={index} className={styles.questionResponse}>
                  <div className={styles.questionText}>{item.question}</div>
                  <div className={styles.answerText}>{item.answer || '-'}</div>
                </div>
              ))}
            </div>
          </div>
        )}
        {/* Default Options Section */}
        {defaultOptions.length > 0 && (
          <div className={styles.group}>
            <h3 className={styles.groupTitle}>
              {t('requiredServices', selectedLocale) || 'Required Services'}
              <span className={styles.defaultBadge}>{t('alwaysIncluded', selectedLocale) || 'Always Included'}</span>
            </h3>
            <div className={styles.groupOptions}>
              {defaultOptions.map(option => (
                <div key={option.id} className={`${styles.selectedOption} ${styles.defaultOption}`}>
                  <div className={styles.optionInfo}>
                    <span className={styles.optionTitle}>{getLocalizedString(option.title, selectedLocale)}</span>
                    {option.days_to_complete !== undefined && (
                      <span className={styles.optionDays}>
                        {option.days_to_complete % 1 === 0 ? option.days_to_complete : option.days_to_complete.toFixed(1)} {t('days', selectedLocale)}
                      </span>
                    )}
                  </div>
                  {option.price && (
                    <span className={styles.optionPrice}>{formatPrice(option.price[selectedCurrency], selectedCurrency)}</span>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* User Selected Options */}
        {groupedSelections.map(({ section, options }) => (
          <div key={section.id} className={styles.group}>
            <h3 className={styles.groupTitle}>{getLocalizedString(section.title, selectedLocale)}</h3>
            <div className={styles.groupOptions}>
              {options.map(option => {
                // Find quantity info for this option
                const withQuantity = selectedOptionsWithQuantities.find(item => item.option.id === option.id);
                const quantity = withQuantity ? withQuantity.quantity : 1;
                const showQuantity = option.allow_multiple_instances && quantity > 1;
                const unitPrice = option.price ? option.price[selectedCurrency] : 0;
                const totalPrice = unitPrice * quantity;

                return (
                  <div key={option.id} className={styles.selectedOption}>
                    <div className={styles.optionInfo}>
                      <span className={styles.optionTitle}>
                        {getLocalizedString(option.title, selectedLocale)}
                        {showQuantity && (
                          <span className={styles.quantityBadge}> ×{quantity}</span>
                        )}
                      </span>
                      {option.days_to_complete !== undefined && (
                        <span className={styles.optionDays}>
                          {option.days_to_complete % 1 === 0 ? option.days_to_complete : option.days_to_complete.toFixed(1)} {t('days', selectedLocale)}
                        </span>
                      )}
                    </div>
                    {option.price && (
                      <span className={styles.optionPrice}>
                        {showQuantity ? (
                          <>
                            {formatPrice(unitPrice, selectedCurrency)} × {quantity} = {formatPrice(totalPrice, selectedCurrency)}
                          </>
                        ) : (
                          formatPrice(unitPrice, selectedCurrency)
                        )}
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ))}

        {/* Hourly Options Section */}
        {hourlyOptions.length > 0 && (
          <div className={styles.group}>
            <h3 className={styles.groupTitle}>
              {t('ongoingCosts', selectedLocale)}
              <span className={styles.hourlyBadge}>{t('hourly_rate', selectedLocale)}</span>
            </h3>
            <div className={styles.groupOptions}>
              {hourlyOptions.map(option => {
                // Find quantity info for this hourly option
                const withQuantity = selectedOptionsWithQuantities.find(item => item.option.id === option.id);
                const quantity = withQuantity ? withQuantity.quantity : 1;
                const showQuantity = option.allow_multiple_instances && quantity > 1;

                return (
                  <div key={option.id} className={`${styles.selectedOption} ${styles.hourlyOption}`}>
                    <div className={styles.optionInfo}>
                      <span className={styles.optionTitle}>
                        {getLocalizedString(option.title, selectedLocale)}
                        {showQuantity && (
                          <span className={styles.quantityBadge}> ×{quantity}</span>
                        )}
                      </span>
                    </div>
                    {option.price && (
                      <span className={styles.hourlyPrice}>
                        +{formatPrice(option.price[selectedCurrency], selectedCurrency)}/{t('hour', selectedLocale)}
                        {showQuantity && <span className={styles.quantityNote}> ({quantity} units)</span>}
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      <div className={styles.totals}>
        <div className={styles.totalRow}>
          <span className={styles.totalLabel}>{t('totalPrice', selectedLocale)}</span>
          <span className={styles.totalPrice}>{formatPrice(totalPrices[selectedCurrency], selectedCurrency)}</span>
        </div>
        <div className={styles.totalRow}>
          <span className={styles.totalLabel}>{t('projectTimeline', selectedLocale)}</span>
          <span className={styles.totalDeadline}>{formatDeadlineLocalized(projectDeadline, selectedLocale)}</span>
        </div>
      </div>

      <div className={styles.actions}>
        {/* Validation Message */}

        <button
          className={styles.exportButton}
          onClick={handleSendToTelegram}
          disabled={isButtonDisabled}
        >
          {isSending ? t('generatingDocument', selectedLocale) || 'Generating document...' : t('sendSummary', selectedLocale)}
        </button>
        {!isValid && hasAnySelections && (
          <div className={styles.validationMessage}>
            {t('requiredSections', selectedLocale)}: {missingSections.map(section => getLocalizedString(section.title, selectedLocale)).join(', ')} {t('shouldBeChosen', selectedLocale)}
          </div>
        )}
      </div>

      {sendStatus === 'success' && (
        <div className={styles.successMessage}>
          ✓ Successfully sent to Telegram!
        </div>
      )}

      {sendStatus === 'error' && (
        <div className={styles.errorMessage}>
          ✗ {errorMessage}
        </div>
      )}
    </div>
  );
};